import React, { useState, useEffect, useCallback } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import { Button } from "react-bootstrap";
import { useHistory, Link } from "react-router-dom";
import { FiEdit2 } from "react-icons/fi";
import { HiOutlineTrash } from "react-icons/hi";
import BootstrapTable from "react-bootstrap-table-next";
import { Pagination } from "@mui/material";
import axios, { get } from "axios";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../../Helper/Breadcrumbs";
import Loader from "../../../Helper/Loader";
import CustomSearch from "../../../Helper/CustomSearch";
import config from "../../../../config";

const PAGE_SIZE = 20;

const tokenKey = {
  headers: { Authorization: "Token " + sessionStorage.getItem("tokenKey") },
};

const toastAttr = {
  theme: "colored",
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const stripHtml = (html) => {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const text = tempDiv.textContent || tempDiv.innerText || "";
  return text.length > 140 ? text.substring(0, 140) + "..." : text;
};

// Backend now sends ISO 8601 for created_at/updated_at (see MandapContentSerializer),
// but this also tolerates the API's older/global "DD/MM/YYYY HH:MM:SS" format so it
// keeps working even if that ever regresses — same workaround already used by the
// sibling blogdata table for the same backend quirk.
const formatDate = (dtStr) => {
  if (!dtStr) return "—";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const custom = dtStr.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (custom) {
    const [, dd, mm, yyyy] = custom;
    return `${dd} ${months[parseInt(mm, 10) - 1]} ${yyyy}`;
  }
  const d = new Date(dtStr);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }
  return "—";
};

const MandapContent = () => {
  const history = useHistory();

  const breadcrumbData = [
    { title: "seobook", href: "/seobook" },
    { title: "Mandap Content Editor", href: "#" },
  ];

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState({ current: 1, total: 1, count: 0 });

  const fetchData = useCallback((search = "", pageNum = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: pageNum, page_size: PAGE_SIZE });
    if (search) params.set("search", search);

    get(`${config.service}/masters/mandap-content/?${params}`, tokenKey)
      .then((res) => {
        const results = res.data?.results ?? [];
        const count = res.data?.count ?? results.length;
        setTableData(results);
        setPage({ current: pageNum, total: Math.ceil(count / PAGE_SIZE) || 1, count });
      })
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = "/";
        else toast.error("Failed to load mandap content", toastAttr);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    fetchData(term, 1);
  };
  const handlePageChange = (_, value) => fetchData(searchTerm, value);

  const renderForm = (row) =>
    history.push({
      pathname: "/seobook/mandapcontentform",
      state: row
        ? { mandap_id: row.mandap, mandap_name: row.mandap_name, editMode: true }
        : {},
    });

  const deleteRow = (row) => {
    if (!window.confirm(`Delete custom content for "${row.mandap_name}"? The entity page will fall back to the auto-generated description.`)) {
      return;
    }
    axios
      .delete(`${config.service}/masters/vendor_mandap/${row.mandap}/content/`, tokenKey)
      .then(() => {
        toast.success("Deleted successfully", toastAttr);
        fetchData(searchTerm, page.current);
      })
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = "/";
        else toast.error("Failed to delete content", toastAttr);
      });
  };

  const toggleActive = (row) => {
    axios
      .put(
        `${config.service}/masters/vendor_mandap/${row.mandap}/content/`,
        { is_active: !row.is_active },
        tokenKey
      )
      .then(() => {
        toast.success(`Marked as ${!row.is_active ? "Active" : "Inactive"}`, toastAttr);
        fetchData(searchTerm, page.current);
      })
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = "/";
        else toast.error("Failed to update status", toastAttr);
      });
  };

  const columns = [
    {
      dataField: "mandap_name",
      text: "Mandapam",
      headerStyle: { width: "200px" },
      style: { verticalAlign: "top" },
      formatter: (cell) => (
        <div
          title={cell}
          style={{
            fontWeight: 600,
            maxWidth: 200,
            lineHeight: "18px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {cell || "—"}
        </div>
      ),
    },
    {
      dataField: "html_content",
      text: "Content Preview",
      style: { verticalAlign: "top" },
      formatter: (cell) => (
        <div
          style={{
            maxWidth: 420,
            color: "#666",
            fontSize: 13,
            lineHeight: "18px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {stripHtml(cell) || "—"}
        </div>
      ),
    },
    {
      dataField: "created_at",
      text: "Created",
      headerStyle: { width: "140px", whiteSpace: "nowrap" },
      style: { verticalAlign: "top" },
      formatter: (cell) => <span style={{ fontSize: 12, color: "#777" }}>{formatDate(cell)}</span>,
    },
    {
      dataField: "updated_at",
      text: "Updated",
      headerStyle: { width: "140px", whiteSpace: "nowrap" },
      style: { verticalAlign: "top" },
      formatter: (cell) => <span style={{ fontSize: 12, color: "#777" }}>{formatDate(cell)}</span>,
    },
    {
      dataField: "is_active",
      text: "Status",
      headerStyle: { width: "110px", whiteSpace: "nowrap" },
      style: { verticalAlign: "top" },
      events: { onClick: (e) => e.stopPropagation() },
      formatter: (cell, row) => (
        <span
          className={`badge ${cell ? "badge-success" : "badge-secondary"}`}
          style={{ cursor: "pointer" }}
          title="Click to toggle"
          onClick={() => toggleActive(row)}
        >
          {cell ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      dataField: "id",
      text: "Actions",
      headerStyle: { width: "100px", whiteSpace: "nowrap" },
      style: { verticalAlign: "top" },
      events: { onClick: (e) => e.stopPropagation() },
      formatter: (cell, row) => (
        <div className="tableActions" style={{ display: "flex", gap: 10 }}>
          <Link to={{ pathname: "/seobook/mandapcontentform", state: { mandap_id: row.mandap, mandap_name: row.mandap_name, editMode: true } }}>
            <FiEdit2 title="Edit" style={{ cursor: "pointer" }} />
          </Link>
          <HiOutlineTrash title="Delete" style={{ cursor: "pointer" }} onClick={() => deleteRow(row)} />
        </div>
      ),
    },
  ];

  const rowEvents = {
    onClick: (e, row) => renderForm(row),
  };

  return (
    <Container className="vendor">
      <Row>
        <Col md={12} className="contBody">
          <Card>
            <CardBody>
              <div className="card__title pageNav">
                <div className="bold-text">
                  <Breadcrumbs data={breadcrumbData} />
                </div>
              </div>

              <Row className="formBtnsDiv mar-b-10" style={{ alignItems: "center" }}>
                <Col md={6} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Button variant="primary" onClick={() => renderForm()}>
                    + Create Content
                  </Button>
                  {page.count > 0 && (
                    <span style={{ fontSize: 13, color: "#888" }}>
                      {page.count} mandap{page.count !== 1 ? "s" : ""} with custom content
                    </span>
                  )}
                </Col>
                <Col md={6} style={{ display: "flex", justifyContent: "flex-end" }}>
                  <CustomSearch callbackFn={handleSearch} />
                </Col>
              </Row>

              <Row className="vendorList">
                <Col md={12}>
                  <Loader loading={loading}>
                    <BootstrapTable
                      bootstrap4
                      wrapperClasses="table-responsive pad-20"
                      data={tableData}
                      columns={columns}
                      keyField="id"
                      hover
                      noDataIndication={
                        <div style={{ textAlign: "center", padding: "32px 0", color: "#aaa" }}>
                          {searchTerm
                            ? `No mandap content found for "${searchTerm}"`
                            : "No custom content yet. Click '+ Create Content' to add some."}
                        </div>
                      }
                      rowEvents={rowEvents}
                      rowStyle={{ cursor: "pointer" }}
                    />
                  </Loader>
                </Col>
              </Row>

              {page.total > 1 && (
                <Row className="mar-t-30 mar-b-20 justify-content-center">
                  <Pagination
                    count={page.total}
                    page={page.current}
                    onChange={handlePageChange}
                    shape="rounded"
                    sx={{
                      "& .Mui-selected": { backgroundColor: "#EC628A !important", color: "#fff !important" },
                      "& .MuiPaginationItem-root:hover": { backgroundColor: "rgba(236,98,138,0.1)" },
                    }}
                  />
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Container>
  );
};

export default MandapContent;
